// lib/persistence.ts — localStorage helpers for scripts, edits, recordings

import { store } from '../store';
import { setEditedScriptLines, setRecordedAudio, setCharacters } from '../store/scriptSlice';
import type { ScriptLine, RecordedAudioEntry } from './scriptParser';

export interface SavedScript {
    id: string;
    name: string;
    text: string;
    lineCount?: number;
    characterCount?: number;
    date?: string;
    editedLines?: ScriptLine[] | null;
    recordedAudio?: RecordedAudioEntry[] | null;
    selectedCharacters?: string[];
    selectedCharacter?: string;
    lastMode?: string;
    updatedAt?: string;
}

function getScriptName() { return store.getState().script.currentScriptName; }

export function getWorkingLines(): ScriptLine[] {
    const { editedScriptLines, parsedLines } = store.getState().script;
    if (editedScriptLines && editedScriptLines.length > 0) {
        return editedScriptLines;
    }
    return parsedLines;
}

export function persistEditedLines(): void {
    const scriptName = getScriptName();
    const { editedScriptLines } = store.getState().script;
    if (!scriptName || !editedScriptLines) return;
    const saved = loadScripts();
    const index = saved.findIndex(s => s.name === scriptName);
    if (index !== -1) {
        saved[index].editedLines = editedScriptLines;
        localStorage.setItem('savedScripts', JSON.stringify(saved));
    } else {
        const editedMap = JSON.parse(localStorage.getItem('editedLinesByName') || '{}');
        editedMap[scriptName] = editedScriptLines;
        localStorage.setItem('editedLinesByName', JSON.stringify(editedMap));
    }
}

export function loadPersistedEdits(): void {
    const scriptName = getScriptName();
    if (!scriptName) return;
    const saved: SavedScript[] = JSON.parse(localStorage.getItem('savedScripts') || '[]');
    const index = saved.findIndex(s => s.name === scriptName);
    if (index !== -1 && saved[index].editedLines) {
        store.dispatch(setEditedScriptLines(saved[index].editedLines!));
        return;
    }
    const editedMap = JSON.parse(localStorage.getItem('editedLinesByName') || '{}');
    if (editedMap[scriptName]) {
        store.dispatch(setEditedScriptLines(editedMap[scriptName]));
    }
}

export function persistRecordedAudio(): void {
    const scriptName = getScriptName();
    const { currentRecordedAudio } = store.getState().script;
    if (!scriptName || !currentRecordedAudio) return;
    const saved: SavedScript[] = JSON.parse(localStorage.getItem('savedScripts') || '[]');
    const index = saved.findIndex(s => s.name === scriptName);
    if (index !== -1) {
        saved[index].recordedAudio = currentRecordedAudio;
        localStorage.setItem('savedScripts', JSON.stringify(saved));
    } else {
        const audioMap = JSON.parse(localStorage.getItem('recordedAudioByName') || '{}');
        audioMap[scriptName] = currentRecordedAudio;
        localStorage.setItem('recordedAudioByName', JSON.stringify(audioMap));
    }
}

export function loadPersistedRecordings(): void {
    const scriptName = getScriptName();
    if (!scriptName) return;
    const saved: SavedScript[] = JSON.parse(localStorage.getItem('savedScripts') || '[]');
    const index = saved.findIndex(s => s.name === scriptName);
    if (index !== -1 && saved[index].recordedAudio) {
        store.dispatch(setRecordedAudio(saved[index].recordedAudio!));
        return;
    }
    const audioMap = JSON.parse(localStorage.getItem('recordedAudioByName') || '{}');
    if (audioMap[scriptName]) {
        store.dispatch(setRecordedAudio(audioMap[scriptName]));
    }
}

export function persistLastMode(mode: string): void {
    const scriptName = getScriptName();
    if (!scriptName) return;
    const saved: SavedScript[] = JSON.parse(localStorage.getItem('savedScripts') || '[]');
    const index = saved.findIndex(s => s.name === scriptName);
    if (index !== -1) {
        saved[index].lastMode = mode;
        localStorage.setItem('savedScripts', JSON.stringify(saved));
    } else {
        const modeMap = JSON.parse(localStorage.getItem('lastModeByName') || '{}');
        modeMap[scriptName] = mode;
        localStorage.setItem('lastModeByName', JSON.stringify(modeMap));
    }
}

export function loadLastMode(): string {
    const scriptName = getScriptName();
    if (!scriptName) return '';
    const saved: SavedScript[] = JSON.parse(localStorage.getItem('savedScripts') || '[]');
    const index = saved.findIndex(s => s.name === scriptName);
    if (index !== -1 && saved[index].lastMode) {
        return saved[index].lastMode!;
    }
    const modeMap = JSON.parse(localStorage.getItem('lastModeByName') || '{}');
    return modeMap[scriptName] || '';
}

export function loadScripts(): SavedScript[] {
    const saved: SavedScript[] = JSON.parse(localStorage.getItem('savedScripts') || '[]');
    let changed = false;
    saved.forEach(s => {
        if (!s.id) {
            s.id = 'script_' + Date.now() + '_' + Math.random().toString(16).slice(2);
            changed = true;
        }
    });
    if (changed) localStorage.setItem('savedScripts', JSON.stringify(saved));
    return saved;
}

export function saveScripts(list: SavedScript[]): void {
    localStorage.setItem('savedScripts', JSON.stringify(list));
}

export function loadSelectedCharacters(scriptEntry: SavedScript, validSet: Set<string> | null): Set<string> {
    let chars: string[] = [];
    if (Array.isArray(scriptEntry.selectedCharacters)) {
        chars = scriptEntry.selectedCharacters;
    } else if (typeof scriptEntry.selectedCharacter === 'string' && scriptEntry.selectedCharacter) {
        chars = [scriptEntry.selectedCharacter];
    }
    const result = new Set(chars);
    if (validSet) {
        for (const c of result) {
            if (!validSet.has(c)) result.delete(c);
        }
    }
    return result;
}

export function getScriptById(id: string): SavedScript | null {
    const scripts = loadScripts();
    return scripts.find(s => s.id === id) || null;
}

export function updateScript(id: string, patch: Partial<SavedScript>): SavedScript | null {
    const scripts = loadScripts();
    const index = scripts.findIndex(s => s.id === id);
    if (index === -1) return null;
    scripts[index] = { ...scripts[index], ...patch, updatedAt: new Date().toISOString() };
    saveScripts(scripts);
    return scripts[index];
}

export function getWorkingCharacterSet(): Set<string> {
    const set = new Set<string>();
    getWorkingLines().forEach(line => {
        if (line.character) set.add(line.character);
    });
    return set;
}

export function rebuildCharactersFromLines(lines: ScriptLine[]): void {
    const chars: string[] = [];
    lines.forEach(line => {
        if (line.character && !chars.includes(line.character)) chars.push(line.character);
    });
    store.dispatch(setCharacters(chars));
}

export function applyRecordedAudioToLines(lines: ScriptLine[], recordedAudio: RecordedAudioEntry[] | null): void {
    if (!lines || !recordedAudio || recordedAudio.length === 0) return;
    let idx = 0;
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].isStageDirection) continue;
        const rec = recordedAudio[idx];
        if (!rec) break;
        lines[i].recordedAudioId = rec.audioId || null;
        idx++;
    }
}

export function syncRecordedAudioFromWorkingLines(): void {
    const lines = getWorkingLines();
    const audio: RecordedAudioEntry[] = lines
        .filter(l => !l.isStageDirection && l.recordedAudioId)
        .map(l => ({ character: l.character || '', text: l.dialogue, audioId: l.recordedAudioId! }));
    store.dispatch(setRecordedAudio(audio));
    persistRecordedAudio();
}
